// 街単位の地図で「重なるピンだけをずらし、N 店なら必ず N 個の個別ピンを出す」ための中核。
//
// 設計: .spec/DESIGN-prefecture-map-dense-area.md §15
//
// **ここは純粋関数だけを置く**。DOM も Leaflet も window も触らないので、`node --test` から
// 直接呼べる（`spec/javascript/geo_map_declutter.test.mjs`）。幾何が正しいかを 15 秒待ちの
// system spec だけで守るのは現実的でないため、梅田・渋谷の実座標由来の画素配置はここで固定する。
//
// 満たす規約:
//  1. **決定的**。同じ入力からは必ず同じ配置が出る（乱数も Date も使わない。入力が整数なら
//     計算も整数で閉じる）。同じ街を 2 回開いて配置が変わらないことの根拠がここにある。
//  2. **重ならない**。置いた矩形どうしは必ず gap 以上離れる。
//  3. **枠の中に収まる**。area からも obstacles（タイトル・閉じるボタン等の固定 UI）からも外れない。
//  4. **動かす量は最小**。真の位置に置けるものは 1px も動かさない。動かすものは真の位置から
//     近い順に候補を試す。

// 矩形の交差判定。gap 未満まで近づいたら交差とみなす（geo_map.js の rectsCollide と同じ規約）。
export function rectsCollide(a, b, gap) {
  return a.left - gap < b.right && b.left - gap < a.right &&
    a.top - gap < b.bottom && b.top - gap < a.bottom;
}

// 矩形が area の内側に gap 以上の余白を持って収まるか。
export function rectInside(rect, area, gap) {
  return rect.left >= area.left + gap && rect.right <= area.right - gap &&
    rect.top >= area.top + gap && rect.bottom <= area.bottom - gap;
}

function rectAt(x, y, footprint) {
  return {
    left: x + footprint.left,
    right: x + footprint.right,
    top: y + footprint.top,
    bottom: y + footprint.bottom
  };
}

// 探索順の候補オフセット。**チェビシェフ距離のリングを外側へ広げ、各リング内は
// ユークリッド距離 → dy → dx の完全順序**で並べる。
//
// 整数格子だけを見るので、浮動小数の丸めで順序が揺れることが原理的に無い。
// 角度を使う螺旋にすると三角関数の誤差で「同じ入力なのに 1px 違う配置」が起きうる。
function candidateOffsets(step, maxRadius) {
  const offsets = [{ dx: 0, dy: 0 }];

  for (let ring = 1; ring * step <= maxRadius; ring += 1) {
    const r = ring * step;
    const onRing = [];

    for (let dy = -r; dy <= r; dy += step) {
      for (let dx = -r; dx <= r; dx += step) {
        // リングの外周だけ（内側は前のリングで見ている）。
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        onRing.push({ dx: dx, dy: dy });
      }
    }

    onRing.sort(function (a, b) {
      const da = a.dx * a.dx + a.dy * a.dy;
      const db = b.dx * b.dx + b.dy * b.dy;
      if (da !== db) return da - db;
      if (a.dy !== b.dy) return a.dy - b.dy;
      return a.dx - b.dx;
    });
    Array.prototype.push.apply(offsets, onRing);
  }

  return offsets;
}

/**
 * 重なるピンをずらして配置する。
 *
 * @param {Array<{id:number, x:number, y:number}>} points
 *        ピンのアンカー（真の位置）。x/y は canvas 左上を原点とする画素。
 * @param {Object} options
 * @param {{left:number,right:number,top:number,bottom:number}} options.footprint
 *        アンカーから見たピンの矩形。下端中央アンカーなら {left:-w/2,right:w/2,top:-h,bottom:0}、
 *        中心アンカーなら {left:-w/2,right:w/2,top:-h/2,bottom:h/2}。
 * @param {number} options.gap        最低間隔
 * @param {{left:number,right:number,top:number,bottom:number}} options.area  置いてよい枠（canvas）
 * @param {Array<Object>} [options.obstacles]  固定 UI の矩形（同じ座標系）
 * @param {number} [options.step]      候補点の刻み
 * @param {number} [options.maxRadius] 探索を打ち切る半径
 * @returns {Array<{id:number, x:number, y:number, dx:number, dy:number, displaced:boolean, placed:boolean}>}
 *        入力と同じ順で返す。`placed:false` は枠内に置き場所が無かったもの（呼び出し側は
 *        これを 'partial' として計測へ残し、黙って重ねない）。
 */
export function computePlacements(points, options) {
  const footprint = options.footprint;
  const gap = options.gap;
  const area = options.area;
  const obstacles = options.obstacles || [];
  const step = options.step || 4;
  const maxRadius = options.maxRadius || 160;
  const offsets = candidateOffsets(step, maxRadius);

  // **並べ替えは (y, x, id) の完全順序**。id は payload 側で一意なので、
  // 同じ画素に落ちた 2 点でも順序が決まる（= 配置が決まる）。
  const order = points.map(function (point, index) { return index; });
  order.sort(function (a, b) {
    const pa = points[a];
    const pb = points[b];
    if (pa.y !== pb.y) return pa.y - pb.y;
    if (pa.x !== pb.x) return pa.x - pb.x;
    return pa.id - pb.id;
  });

  const placedRects = [];
  const results = new Array(points.length);

  order.forEach(function (index) {
    const point = points[index];
    let chosen = null;

    for (let i = 0; i < offsets.length; i += 1) {
      const offset = offsets[i];
      const rect = rectAt(point.x + offset.dx, point.y + offset.dy, footprint);
      if (!rectInside(rect, area, gap)) continue;

      let blocked = false;
      for (let j = 0; j < obstacles.length && !blocked; j += 1) {
        if (rectsCollide(rect, obstacles[j], gap)) blocked = true;
      }
      for (let j = 0; j < placedRects.length && !blocked; j += 1) {
        if (rectsCollide(rect, placedRects[j], gap)) blocked = true;
      }
      if (blocked) continue;

      chosen = offset;
      placedRects.push(rect);
      break;
    }

    if (chosen) {
      results[index] = {
        id: point.id,
        x: point.x + chosen.dx,
        y: point.y + chosen.dy,
        dx: chosen.dx,
        dy: chosen.dy,
        displaced: chosen.dx !== 0 || chosen.dy !== 0,
        placed: true
      };
      return;
    }

    // 置き場所が無かった。**真の位置へ戻して placed:false を立てる**。
    // 適当な場所へ押し込むと「重なっていないように見えて実は重なっている」状態を作る。
    results[index] = {
      id: point.id, x: point.x, y: point.y, dx: 0, dy: 0, displaced: false, placed: false
    };
  });

  return results;
}
